import { LocalizationService } from '@abp/ng.core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateUpdateCommunicationDto, CommunicationDto } from '../../proxy/communications/models';
import { CommunicationService } from '../../proxy/communications/communication.service';
import { StorageService } from '../../proxy/storage/storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_ICONS } from 'src/app/shared/icons/app-icons';
import { ConfirmationModalService } from 'src/app/shared/services/confirmation-modal.service';
import { MetroToastService } from 'src/app/shared/services/metro-toast.service';

@Component({
  selector: 'app-communication-form',
  templateUrl: './communication-form.component.html',
  standalone: false
})
export class CommunicationFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  communicationId?: string;
  iconBack = APP_ICONS.arrow;
  selectedFile: File | null = null;
  existingFileName: string | null = null;
  isUploading = false;
  APP_ICONS = APP_ICONS;
  imagePreviewUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private communicationService: CommunicationService,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private router: Router,
    private localizationService: LocalizationService,
    private confirmation: ConfirmationModalService,
    private toast: MetroToastService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.communicationId = this.route.snapshot.paramMap.get('id');

    if (this.communicationId) {
      this.isEditMode = true;
      this.communicationService.get(this.communicationId).subscribe({
        next: (comm) => {
          this.patchForm(comm);
        },
        error: (err) => {
          this.toast.error('No se pudo cargar la comunicación.', '::Error');
          console.error(err);
        }
      });
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      message: ['', [Validators.required, Validators.maxLength(500)]],
      isActive: [true],
      imageFileId: [null as number | null]
    });
  }

  private patchForm(comm: CommunicationDto) {
    this.form.patchValue({
      message: comm.message,
      isActive: comm.isActive,
      imageFileId: comm.imageFileId
    });
    if (comm.imageFileId) {
      this.existingFileName = comm.imageFileName || 'imagen_adjuntada.jpg';
      this.imagePreviewUrl = comm.imageUrl || null;
    }
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (control?.invalid && control?.touched) {
      if (control.errors?.['required']) return '::ThisFieldIsRequired';
      if (control.errors?.['maxlength']) return 'El mensaje no puede superar los 500 caracteres';
    }
    return undefined;
  }

  async onImageSelected(files: File[]) {
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validations
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.toast.error('El archivo no puede superar los 5 MB', '::Error');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        this.toast.error('Solo se permiten archivos JPG, PNG, SVG o WebP', '::Error');
        return;
      }

      this.selectedFile = file;
      
      // Auto-upload the image
      await this.uploadImage();
    }
  }

  async uploadImage() {
    if (!this.selectedFile) return;

    this.isUploading = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);

    try {
      const fileId = await this.storageService.uploadFileByFile(formData).toPromise();
      this.form.patchValue({ imageFileId: fileId });
      this.toast.success('Imagen subida correctamente', '::Success');
    } catch (error) {
      this.toast.error('Error al subir la imagen', '::Error');
      console.error(error);
      this.removeImage();
    } finally {
      this.isUploading = false;
    }
  }

  removeImage() {
    this.selectedFile = null;
    this.existingFileName = null;
    this.imagePreviewUrl = null;
    this.form.patchValue({ imageFileId: null });
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const dto: CreateUpdateCommunicationDto = {
      message: formValue.message,
      isActive: formValue.isActive,
      imageFileId: formValue.imageFileId
    };
    
    if (this.isEditMode && this.communicationId) {
      this.communicationService.update(this.communicationId, dto).subscribe({
        next: () => {
          this.toast.success('::SavedSuccessfully', '::Success');
          this.handleSuccess();
        },
        error: (err) => {
          this.toast.error('Ocurrió un error al actualizar la comunicación.', '::Error');
          console.error(err);
        }
      });
    } else {
      this.communicationService.create(dto).subscribe({
        next: () => {
          this.toast.success('::SavedSuccessfully', '::Success');
          this.handleSuccess();
        },
        error: (err) => {
          this.toast.error('Ocurrió un error al crear la comunicación.', '::Error');
          console.error(err);
        }
      });
    }
  }

  private handleSuccess() {
    this.router.navigate(['/communications']);
  }

  cancel() {
    this.router.navigate(['/communications']);
  }

  deleteCommunication() {
    if (!this.communicationId) return;
    
    this.confirmation.confirm({
      title: this.localizationService.instant('::Delete'),
      message: '::AreYouSureToDelete',
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.communicationService.delete(this.communicationId!).subscribe({
        next: () => {
          this.toast.success('::SuccessfullyRemoved', '::Success');
          this.router.navigate(['/communications']);
        },
        error: (err) => {
          this.toast.error('Ocurrió un error al eliminar.', '::Error');
          console.error(err);
        }
      });
    });
  }
}
